import { useFlagMaxLifetimeEnforcementEnabled } from "@/hooks/use-flag";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { getLockState } from "@/utils/lock-state";
import { MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import type { LifetimeModalMode } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { ConnectError } from "@connectrpc/connect";
import { PolicyEnforcedFailedPreconditionDetails } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState } from "react";

/**
 * Encapsulates the max-lifetime enforcement flow for starting environments.
 *
 * Three modal modes depending on flag + strict + lock state:
 *
 * 1. Flag OFF, exceeded → client-side block → modal "client-enforced" (shows "Start anyway")
 * 2. Flag ON, strict ON, exceeded → server rejects → modal "server-enforced" (no restart)
 * 3. Flag ON, strict OFF, exceeded → pre-start check → modal "non-compliant" (shows "Start anyway")
 */
export function useLifetimeEnforcement(environment: PlainEnvironment) {
    const { value: enforcementEnabled } = useFlagMaxLifetimeEnforcementEnabled();
    const { data: policies } = useGetOrganizationPolicies();
    const strict = policies?.maximumEnvironmentLifetimeStrict ?? false;
    const lockState = getLockState(environment.metadata?.lockdownAt);

    const [showModal, setShowModal] = useState(false);
    const [mode, setMode] = useState<LifetimeModalMode>("client-enforced");

    /**
     * Call before starting. Returns true if the start should proceed,
     * false if a modal was shown (caller should not start).
     */
    const checkBeforeStart = useCallback((): boolean => {
        if (enforcementEnabled && !strict && lockState === "blocked") {
            setMode("non-compliant");
            setShowModal(true);
            return false;
        }
        return true;
    }, [enforcementEnabled, strict, lockState]);

    const handleStartError = useCallback((error: unknown): boolean => {
        // Client-side check (enforcement flag disabled)
        if (error instanceof MaximumEnvironmentLifetimeExceededError) {
            setMode("client-enforced");
            setShowModal(true);
            return true;
        }
        // Server-side enforcement error
        if (error instanceof ConnectError) {
            const details = error.findDetails(PolicyEnforcedFailedPreconditionDetails);
            const isLifetimeEnforcement = details.some(
                (d) => d.policyName === "maximum_environment_lifetime_strict",
            );
            if (isLifetimeEnforcement) {
                setMode("server-enforced");
                setShowModal(true);
                return true;
            }
        }
        return false;
    }, []);

    return {
        enforcementEnabled,
        checkBeforeStart,
        handleStartError,
        modalProps: {
            open: showModal,
            onClose: () => setShowModal(false),
            mode,
        },
    };
}
